import { useEffect, useRef, useState } from "react";

import { CSSTransition } from "react-transition-group";

import ChevronDown from "@/assets/icons/chevron-down.svg";
import { HARD_BREAK_MOBILE_PX } from "@/constants";
import { useGlobalAudioPlayer } from "@/contexts/GlobalAudioPlayerContext";
import useBreakpoint from "@/hooks/useBreakpoint";
// import useRelativeMousePosition from "@/hooks/useRelativeMousePosition";
// import { AudioMention } from "@/types/Mention";
import { clamp, cx, map } from "@/utils";
import Clickable from "@components/Clickable";
import Dialog, { DialogProps } from "@components/Dialog";
import PlayButton from "@components/PlayButton";
import Typography from "@components/Typography";

import Cover from "../Episode/partials/Cover";

import styles from "./AudioPlayer.module.css";

type AudioPlayerProps = React.HTMLProps<HTMLDivElement>;

const Compact: React.FunctionComponent<
  AudioPlayerProps & { onExpand: () => void }
> = ({ className, onExpand, ...props }) => {
  const {
    audio,
    playing,
    errored,
    loading,
    currentTime = 0,
  } = useGlobalAudioPlayer();

  const [open, setOpen] = useState<boolean>(audio !== null);
  const progress = useRef<HTMLDivElement>(null);

  // const audioMentions = (audio?.episode.audioMentions as AudioMention[]) || [];
  // const activeMention = audioMentions.find(
  //   (m) =>
  //     m.context_start_timing <= currentTime &&
  //     m.context_end_timing >= currentTime
  // );
  // const currentPlaylistIndex = audioMentions?.findIndex(
  //   (p) => activeMention.public_id === p.public_id
  // );

  // Side-effects
  const [prevAudio, setPrevAudio] = useState(audio);
  if (audio !== prevAudio) {
    setPrevAudio(audio);

    if (audio !== null) {
      setOpen(true);
    }
  }

  useEffect(() => {
    if (progress.current && audio) {
      const beginsAt = 0;
      const stopsAt = 1000;

      const percentage = clamp(
        map(currentTime, beginsAt, stopsAt, 0, 100),
        0,
        100
      );
      progress.current.style.transform = `translateX(${percentage}%)`;
    }
  }, [currentTime, audio]);

  return (
    <div
      {...props}
      className={cx(
        styles.root,
        styles.compact,
        { [styles.open]: open },
        className
      )}
      onClick={onExpand}
    >
      <div className={styles.preview}>
        <div className={styles.content}>
          {audio?.episode && (
            <Cover className={styles.cover} episode={audio?.episode} />
          )}
          <div>
            <Typography className={styles.show} block margin="none">
              <strong>{audio?.episode?.show}</strong>
            </Typography>
            <Typography className={styles.title} block margin="none">
              {audio?.episode?.title}
            </Typography>
          </div>
          <PlayButton
            playing={playing}
            error={errored}
            loading={loading}
            className={styles.play}
            monochrome
            onClick={(e) => {
              // prevent click on playbutton opening the expanded version
              e.stopPropagation();
              if (playing) {
                audio?.pause();
              } else {
                audio?.play();
              }
            }}
          />
        </div>
        {/* <div className={styles.dummy}>
          <div className={styles.progress} ref={progress} />
        </div> */}
        {/* {audio?.episode && (
          <Typography variant="small" align="center" block>
            {`${currentPlaylistIndex + 1} ${L.MENTIONS_PLAYER_COUNT_OF} ${
              audioMentions.length
            } ${pluralize(
              audioMentions.length,
              L.MENTIONS_SINGULAR,
              L.MENTIONS_PLURAL
            )}`}
          </Typography>
        )} */}
      </div>
    </div>
  );
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Extended: React.FunctionComponent<AudioPlayerProps & DialogProps> = ({
  onClose,
  open = false,
  className,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    audio,
    // currentTime = 0,
    loading,
    playing,
    errored,
  } = useGlobalAudioPlayer();

  // refs and states
  // const progress = useRef<HTMLDivElement>(null);
  // const audioMentions = (audio?.episode.audioMentions as AudioMention[]) || [];
  // const activeMention = audioMentions.find(
  //   (m) =>
  //     m.context_start_timing <= currentTime &&
  //     m.context_end_timing >= currentTime
  // );
  // const totalLength = (activeMention || audioMentions[0])?.total_length || 0;
  // const currentPlaylistIndex = audioMentions?.findIndex(
  //   (p) => activeMention.public_id === p.public_id
  // );

  // Booleans for  forward/backward buttons being active or not
  // const disableBackward = loading || currentPlaylistIndex <= 0;
  // const disableForward =
  //   loading ||
  //   audioMentions.length <= 0 ||
  //   currentPlaylistIndex === audioMentions.length - 1;

  // hooks

  // Handle click on the scrubber to seek
  // const { ref: scrubberRef } =
  //   useRelativeMousePosition(({ width = 0, x = 0 }) => {
  //     if (audio && activeMention) {
  //       const timestamp = Math.abs(
  //         clamp(map(x, 0, width, 0, totalLength), 0, totalLength)
  //       );
  //       timestamp && audio?.seek(timestamp);
  //     }
  //   }) || {};

  if (!audio) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <CSSTransition
        nodeRef={ref}
        in={open}
        timeout={300}
        mountOnEnter
        appear={true}
        unmountOnExit
        classNames={{
          appear: styles.appear,
          appearActive: styles.appearActive,
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
      >
        <div
          className={cx(styles.root, styles.extended, className)}
          {...props}
          ref={ref}
        >
          <div className={styles.actions}>
            <Clickable onClick={onClose} className={styles.close}>
              <ChevronDown />
            </Clickable>
          </div>
          <div className={styles.content}>
            {audio.episode && (
              <Cover className={styles.bigcover} episode={audio.episode} />
            )}
            <div>
              <Typography block margin="none">
                <strong>{audio.episode.show}</strong>
              </Typography>
              <Typography block margin="none">
                {audio.episode.title}
              </Typography>
            </div>
          </div>
          {/* <div className={styles.timeline}>
            <Typography variant="small" align="left">
              {durationFromSeconds(currentTime)}
            </Typography>
            <div className={styles.bigdummy} ref={scrubberRef}>
              <div className={styles.progress} ref={progress} />
            </div>
            <Typography variant="small" align="right">
              {durationFromSeconds(audio.episode.mentions[0].total_length)}
            </Typography>
          </div> */}
          {/* <Typography variant="caption" align="center" block>
            {`${Math.max(currentPlaylistIndex + 1, 1)} ${
              L.MENTIONS_PLAYER_COUNT_OF
            } ${audioMentions.length} ${pluralize(
              audioMentions.length,
              L.MENTIONS_SINGULAR,
              L.MENTIONS_PLURAL
            )}`}
          </Typography> */}
          <div className={styles.controls}>
            {/* <Clickable
              className={styles.jump}
              disabled={disableBackward}
              onClick={() => {
                audio.play();
              }}
            >
              <RewindIcon />
            </Clickable> */}
            <PlayButton
              monochrome
              className={styles.playBig}
              playing={playing}
              error={errored}
              loading={loading}
              onClick={() => {
                if (playing) {
                  audio.pause();
                } else if (audio.episode) {
                  audio.play();
                }
              }}
            />
            {/* <Clickable
              className={styles.jump}
              disabled={disableForward}
              onClick={() => {
                audio.play();
              }}
            >
              <FastForwardIcon />
            </Clickable> */}
          </div>
        </div>
      </CSSTransition>
    </Dialog>
  );
};

const AudioPlayer: React.FC<AudioPlayerProps> = (props) => {
  const [_expanded, setExpanded] = useState<boolean>(false);

  return (
    <>
      <Compact onExpand={() => setExpanded(true)} {...props} />
      {/* <Extended onClose={() => setExpanded(false)} open={expanded} {...props} /> */}
    </>
  );
};

const AudioPlayerResponsive = (props: AudioPlayerProps) => {
  const ref = useRef<typeof document.body>(document.body);
  const mobile = useBreakpoint(ref, HARD_BREAK_MOBILE_PX, false);
  return mobile ? <AudioPlayer {...props} /> : null;
};
export default AudioPlayerResponsive;
